import GIS from "../app";
import PointerInteraction from 'ol/interaction/Pointer';
import Point from "ol/geom/Point";
import Feature from "ol/Feature";
import {getVectorContext} from 'ol/render';
import {easeOut} from 'ol/easing';
import {Circle as CircleStyle, Stroke, Fill, Style} from 'ol/style';
import {unByKey} from 'ol/Observable';
import OlOverlay from 'ol/Overlay';
import {transform} from "ol/proj";
import CoordinatesPanel from "../controls/coordinatespanel.ts";
import {InteractionOptions} from "../configuration/map.ts";
import {LayerType} from "../configuration/layer.ts";
import {IdentifyOptions} from "../general/options.ts";
import {VectorSourceEvent} from "ol/source/Vector";
import {Coordinate} from "ol/coordinate";
import {IdentifyCallback} from "../general/callback.ts";
import {MapBrowserEvent} from "ol";
import {ControlExtendedFunctions} from "../configuration/control.ts";
import {Control as olControl} from "ol/control";
import {EventsKey} from "ol/events";
import RenderEvent from "ol/render/Event";

class Identify extends PointerInteraction {
    private readonly GIS: GIS;

    private readonly layerName: string;
    private readonly layer: LayerType | undefined;
    private readonly identifyOptions: InteractionOptions;
    private readonly duration!: number;
    private callbackOptions: IdentifyOptions;
    private callback: IdentifyCallback | undefined;
    private removeOverlay: () => void;
    private eventStack: string[];
    private marker:OlOverlay | undefined = undefined;

    /** Press-and-hold state — see `holdDuration` in InteractionOptions. */
    private holdTimer: number | null = null;
    private holdStartPixel: [number, number] | null = null;
    private holdOverlay: OlOverlay | null = null;
    /** Tracks active pointer IDs so a second finger (pinch-zoom) cancels the hold. */
    private activePointers: Set<number> = new Set();
    /** True while we have suppressed the native long-press menu / iOS callout on the viewport. */
    private holdGuardsInstalled: boolean = false;

    constructor(options:InteractionOptions, GIS: GIS) {
        super({
            handleEvent: event => this.handleEvent(event)
        });

        this.GIS = GIS;

        this.layerName = 'identify_point';
        this.identifyOptions = options || {};

        this.layer = this.identifyOptions.layer ? this.GIS.Layer.getLayer(this.identifyOptions.layer) : undefined;
        this.duration = 1500;
        this.callbackOptions = {};
        this.callback = () => {
        };
        this.removeOverlay = () => {
        };
        this.eventStack = [];

        this.setLayerListener();
    }

    private setLayerListener = () => {
        if (this.layer) {
            const source = this.layer.getSource();
            source.on('addfeature', (event:VectorSourceEvent) => {
                const feature = event.feature as Feature<Point>;
                const geometry = feature?.getGeometry();
                if (geometry !== undefined) {
                    const viewCoordinates = geometry.getCoordinates() as Coordinate;

                    if (this.callbackOptions.epsg) {
                        const transformedCoordinates = transform(viewCoordinates, this.GIS.View.olObject.getProjection(), this.callbackOptions.epsg);
                        this.callback?.(transformedCoordinates[0], transformedCoordinates[1]);
                    } else {
                        this.callback?.(viewCoordinates[0], viewCoordinates[1]);
                    }

                    feature && this.flash(feature);
                }
            });
        }
    }

    public markerToCoordinates = (coordinates: Coordinate) => {
        if (!this.marker) {
        } else {
            this.removeOverlay();
        }

        const feature = new Feature({
            name: this.layerName,
            id: 1
        });
        feature.setGeometry(new Point(coordinates));
        this.createMarker(feature as Feature<Point>);
        if (this.marker) {
            this.GIS.Map.olObject!.addOverlay(this.marker);
        }
    }

    public setCallback = (options?:IdentifyOptions, callback?:IdentifyCallback) => {
        this.callback = callback;

        if (options) {
            this.callbackOptions = options;
        }

        // Hold-related settings may have just changed (enabled, disabled,
        // duration adjusted, scope flipped). Tear down any in-flight hold
        // and its visual feedback so the next pointerdown starts from a
        // clean state with the new effective settings.
        this.cancelHold();
        if (this.activePointers.size === 0) {
            this.removeHoldGuards();
        }
    };

    /**
     * Effective hold-duration, in ms. Returns the runtime override set via
     * {@link setCallback} when present, otherwise the value from the original
     * interaction config. `0` and `undefined` both disable the gate.
     */
    private effectiveHoldDuration = (): number | undefined => {
        return this.callbackOptions.holdDuration ?? this.identifyOptions.holdDuration;
    };

    private effectiveHoldAppliesTo = (): 'touch' | 'mouse' | 'all' => {
        return this.callbackOptions.holdAppliesTo
            ?? this.identifyOptions.holdAppliesTo
            ?? 'touch';
    };

    private effectiveHoldMoveTolerance = (): number => {
        return this.callbackOptions.holdMoveTolerance
            ?? this.identifyOptions.holdMoveTolerance
            ?? 6;
    };

    public handleEvent = (event:MapBrowserEvent<any>): boolean => {
        // Keep track of active pointers so we can detect pinch (second finger)
        // and cancel any armed hold.
        this.trackPointers(event);

        // When holdDuration is configured AND the current input matches the
        // configured scope, route this event through the hold-gate. Pan/zoom
        // still works because we always return true from handleEvent — the
        // gate only decides whether and when to fire the identify callback.
        if (this.isHoldGated(event)) {
            this.handleHoldEvent(event);
            // A gated input must never fire identify through the click path —
            // identify only fires via the hold timer. Swallow the trailing (or
            // bare) `singleclick` regardless of whether a hold was armed: a
            // singleclick with no preceding `pointerdown` (e.g. a synthesized
            // click, or a mouse click under `holdAppliesTo: 'all'`) still
            // arrives gated and must be ignored here.
            if (event.type === 'singleclick') {
                return true;
            }
            // For pointer events that belong to the hold-gate, don't fall
            // through to the click path. (singleclick is the only event the
            // click path normally acts on, and we handled it just above.)
            if (event.type === 'pointerdown' || event.type === 'pointermove' ||
                event.type === 'pointerdrag' || event.type === 'pointerup' ||
                event.type === 'pointercancel') {
                return true;
            }
        }

        let singleClickCondition = false;

        if (this.identifyOptions.eventType === "pointers") {
            const iOSpointers = ["pointermove", "pointerup", "pointerdrag", "pointerdown"];
            this.eventStack.push(event.type);
            const eventStackLength = this.eventStack.length;

            if (this.eventStack[eventStackLength - 1] === 'singleclick') {
                singleClickCondition = true;

                /** iOS troubles with singleclick **/
                this.eventStack = [];
            }

            if ((
                    this.eventStack[eventStackLength - 1] === 'pointerup' &&
                    this.eventStack[eventStackLength - 2] === 'pointerdown') ||
                (
                    eventStackLength < 31 &&
                    this.eventStack[0] === 'pointerdown' &&
                    this.eventStack[eventStackLength - 1] === 'pointerup' &&
                    this.containsOnly(iOSpointers, this.eventStack)
                )
            ) {
                singleClickCondition = true;

                /** iOS troubles with singleclick **/
                this.eventStack = [];
            } else if (
                this.eventStack.length >= 31 &&
                this.eventStack[0] === 'pointerdown' &&
                this.eventStack[eventStackLength - 1] === 'pointerup'
            ) {
                this.eventStack = [];
            }
        } else {
            if (event.type === 'singleclick') {
                singleClickCondition = true;
            }
        }

        if (singleClickCondition) {
            this.triggerIdentify(event);
        }
        return true;
    };

    /**
     * Fire the identify callback for the given event. Shared by the
     * single-click path and the hold-timer path so both end up with identical
     * behaviour (marker, mouse-position panel, layer source update).
     */
    private triggerIdentify = (event: MapBrowserEvent<any>) => {
        if (!this.layer) return;

        const coordinate = this.getCoordinate(event);

        const source = this.layer.getSource();
        source.clear();

        this.removeOverlay = () => {
            this.marker && this.GIS.Map.olObject!.removeOverlay(this.marker);
            this.marker = undefined;
        }

        if (this.identifyOptions.everyClick === true) {
            this.removeOverlay();
        }

        if (!this.marker) {
            const feature = new Feature({
                name: this.layerName,
                id: 1
            });
            feature.setGeometry(new Point(coordinate));
            source.addFeature(feature);

            const mousePositionControl = this.GIS.Control.getControl('mouseposition');
            if (mousePositionControl && mousePositionControl instanceof CoordinatesPanel) {
                const coordinatesPanel = this.GIS.Control.getControl('mouseposition') as olControl & ControlExtendedFunctions;
                coordinatesPanel.cancelEditing();
                coordinatesPanel.cancelErrorStatus();
                coordinatesPanel.coordinatesProcessing(this.GIS.View.olObject.getProjection().getCode(), coordinate);
            }
        } else {
            this.removeOverlay();
        }
    };

    /**
     * Returns true if the given event should be subject to the press-and-hold
     * gate: `holdDuration` must be configured, and the current input type must
     * fall within `holdAppliesTo` (defaults to `'touch'`).
     */
    private isHoldGated = (event: MapBrowserEvent<any>): boolean => {
        const hold = this.effectiveHoldDuration();
        if (hold === undefined || hold <= 0) return false;

        const scope = this.effectiveHoldAppliesTo();
        if (scope === 'all') return true;

        const orig = event.originalEvent as PointerEvent | undefined;
        const pointerType = orig?.pointerType;

        // Synthesized events without a pointerType (rare) — if any input type
        // is gated, treat as gated to be safe. The trailing singleclick of a
        // gated tap normally carries pointerType through originalEvent.
        if (!pointerType) return true;

        if (scope === 'touch') return pointerType === 'touch' || pointerType === 'pen';
        if (scope === 'mouse') return pointerType === 'mouse';
        return false;
    };

    /**
     * Arm/cancel/fire the press-and-hold based on the incoming pointer event.
     * Does NOT short-circuit pan/zoom — handleEvent always returns true.
     */
    private handleHoldEvent = (event: MapBrowserEvent<any>) => {
        if (event.type === 'pointerdown') {
            // A second finger means the user is pinch-zooming — definitely
            // not trying to select a property. Cancel any armed hold.
            if (this.activePointers.size > 1) {
                this.cancelHold();
                return;
            }
            this.startHold(event);
            return;
        }

        if (event.type === 'pointermove' || event.type === 'pointerdrag') {
            if (!this.holdStartPixel) return;
            const dx = event.pixel[0] - this.holdStartPixel[0];
            const dy = event.pixel[1] - this.holdStartPixel[1];
            const tol = this.effectiveHoldMoveTolerance();
            if (dx * dx + dy * dy > tol * tol) {
                this.cancelHold();
            }
            return;
        }

        if (event.type === 'pointerup' || event.type === 'pointercancel') {
            // Released before the timer elapsed — short tap, not a select.
            if (this.holdTimer !== null) {
                this.cancelHold();
            }
            // Tear down the native-long-press guards once the user has
            // fully lifted off (trackPointers already removed this pointer).
            if (this.activePointers.size === 0) {
                this.removeHoldGuards();
            }
            return;
        }
    };

    private startHold = (event: MapBrowserEvent<any>) => {
        // Defensive: clear any prior armed state before arming a new one.
        this.cancelHold();
        this.holdStartPixel = [event.pixel[0], event.pixel[1]];
        // Block the native long-press behaviours BEFORE the OS timer elapses
        // (Android contextmenu, iOS text-selection callout/magnifier).
        this.installHoldGuards();
        this.createHoldOverlay(event);

        // The pointerdown event carries the coordinate we want identify to
        // fire at — capture it so we can still use it after the timeout.
        const heldEvent = event;
        const duration = this.effectiveHoldDuration()!;
        this.holdTimer = window.setTimeout(() => {
            this.holdTimer = null;
            this.removeHoldOverlay();
            this.holdStartPixel = null;
            // Hold completed — fire identify at the original press location.
            this.triggerIdentify(heldEvent);
        }, duration);
    };

    private cancelHold = () => {
        if (this.holdTimer !== null) {
            window.clearTimeout(this.holdTimer);
            this.holdTimer = null;
        }
        this.removeHoldOverlay();
        this.holdStartPixel = null;
    };

    /**
     * Suppress the browser's native long-press affordances during an armed
     * hold:
     *   - Android Chrome / Firefox fire a `contextmenu` event ~500ms into a
     *     long press; preventDefault() stops the menu from appearing while
     *     leaving consumer `Map.setCallback('contextmenu', ...)` listeners
     *     free to run (preventDefault doesn't block propagation).
     *   - iOS Safari shows a text-selection callout / magnifier on long press;
     *     `-webkit-touch-callout: none` and `user-select: none` suppress it.
     *
     * Guards are scoped to the viewport, applied on the pointerdown that
     * arms a hold, and removed when the last active pointer lifts — so they
     * don't interfere with anything outside the gesture.
     */
    private installHoldGuards = () => {
        if (this.holdGuardsInstalled) return;
        const viewport = this.GIS.Map.olObject?.getViewport();
        if (!viewport) return;
        viewport.style.setProperty('-webkit-touch-callout', 'none');
        viewport.style.setProperty('-webkit-user-select', 'none');
        viewport.style.setProperty('user-select', 'none');
        viewport.addEventListener('contextmenu', this.suppressContextMenu);
        this.holdGuardsInstalled = true;
    };

    private removeHoldGuards = () => {
        if (!this.holdGuardsInstalled) return;
        const viewport = this.GIS.Map.olObject?.getViewport();
        if (viewport) {
            viewport.style.removeProperty('-webkit-touch-callout');
            viewport.style.removeProperty('-webkit-user-select');
            viewport.style.removeProperty('user-select');
            viewport.removeEventListener('contextmenu', this.suppressContextMenu);
        }
        this.holdGuardsInstalled = false;
    };

    private suppressContextMenu = (e: Event) => {
        e.preventDefault();
    };

    private trackPointers = (event: MapBrowserEvent<any>) => {
        const orig = event.originalEvent as PointerEvent | undefined;
        const id = orig?.pointerId;
        if (id === undefined) return;
        if (event.type === 'pointerdown') {
            this.activePointers.add(id);
        } else if (event.type === 'pointerup' || event.type === 'pointercancel') {
            this.activePointers.delete(id);
        }
    };

    private createHoldOverlay = (event: MapBrowserEvent<any>) => {
        const duration = this.effectiveHoldDuration()!;
        const size = 44;
        const r = 16;
        const cx = size / 2;
        const cy = size / 2;
        const circumference = 2 * Math.PI * r;

        const container = document.createElement('div');
        container.className = 'cfm-identify-hold-ring';
        container.style.pointerEvents = 'none';
        container.innerHTML =
            `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="display:block;">` +
                `<circle cx="${cx}" cy="${cy}" r="${r}" ` +
                    `fill="rgba(17,139,214,0.10)" ` +
                    `stroke="rgba(81,81,81,0.35)" stroke-width="2"/>` +
                `<circle class="cfm-identify-hold-ring__progress" cx="${cx}" cy="${cy}" r="${r}" ` +
                    `fill="none" ` +
                    `stroke="rgba(17,139,214,0.95)" stroke-width="3" ` +
                    `stroke-linecap="round" ` +
                    `stroke-dasharray="${circumference}" ` +
                    `stroke-dashoffset="${circumference}" ` +
                    `transform="rotate(-90 ${cx} ${cy})" ` +
                    `style="transition: stroke-dashoffset ${duration}ms linear;"/>` +
            `</svg>`;

        this.holdOverlay = new OlOverlay({
            element: container,
            position: this.getCoordinate(event),
            positioning: 'center-center',
            stopEvent: false,
        });
        this.GIS.Map.olObject!.addOverlay(this.holdOverlay);

        // Trigger the CSS transition on the next frame.
        requestAnimationFrame(() => {
            const progress = container.querySelector(
                '.cfm-identify-hold-ring__progress'
            ) as SVGCircleElement | null;
            if (progress) progress.style.strokeDashoffset = '0';
        });
    };

    private removeHoldOverlay = () => {
        if (this.holdOverlay) {
            this.GIS.Map.olObject!.removeOverlay(this.holdOverlay);
            this.holdOverlay = null;
        }
    };

    private containsOnly = (array1: string[], array2: string[]) => {
        return !array2.some(elem => !array1.includes(elem))
    }

    private getCoordinate = (event:MapBrowserEvent<any>) => {
        let coordinate;
        if (event.coordinate) {
            coordinate = event.coordinate;
        } else {
            coordinate = this.GIS.Map.olObject!.getEventCoordinate(event as unknown as MouseEvent);
        }

        return coordinate;
    };

    private createMarker = (feature:Feature<Point>) => {
        const element = document.createElement('I');
        element.className = 'fas fa-map-marker-question cfm-identify fa-lg';

        const mousePositionControl = this.GIS.Control.getControl('mouseposition');
        if (mousePositionControl && mousePositionControl instanceof CoordinatesPanel) {
            const coordinatesPanel = this.GIS.Control.getControl('mouseposition') as olControl & ControlExtendedFunctions;
            coordinatesPanel.cancelEditing();
        }

        this.marker = new OlOverlay({
            position: feature.getGeometry()?.getCoordinates(),
            positioning: 'center-center',
            element: element,
            stopEvent: false
        });
    };

    public getLayer = () => {
        return this.layer;
    }

    public hasIconOverlay = () => {
        return !!this.identifyOptions.icon
    }

    public removePointer = () => {
        this.removeOverlay();
        this.layer && this.layer.getSource().clear();
    }

    private flash = (feature: Feature<Point>) => {
        const start = new Date().getTime();
        let listenerKey: EventsKey | EventsKey[];

        const animate = (event: RenderEvent) => {
            const vectorContext = getVectorContext(event);
            if (!event.frameState) return;

            const frameState = event.frameState;
            const flashGeom = feature.getGeometry()?.clone();
            const elapsed = frameState.time - start;
            const elapsedRatio = elapsed / this.duration;
            // radius will be 5 at start and 30 at end.
            const radius = easeOut(elapsedRatio) * 10 + 5;
            const opacity = easeOut(Math.max(0, 1 - elapsedRatio));
            const opacityFill = easeOut(Math.max(0, 0.4 - elapsedRatio));

            const style = new Style({
                image: new CircleStyle({
                        radius: radius,
                        fill: new Fill({ color: `rgba(17,139,214, ${opacityFill})` }),
                        stroke: new Stroke({
                        color: `rgba(81,81,81, ${opacity})`,
                        width: 0.5 + opacity
                    })
                })
            });

            if (flashGeom) {
                const flashFeature = new Feature({ geometry: flashGeom });
                vectorContext.drawFeature(flashFeature, style);
            }

            if (elapsed > this.duration) {
                unByKey(listenerKey);                return;
            }
            // Openlayers will continue postrender
            this.GIS.Map.olObject!.render();
        }

        if (this.identifyOptions.animate && this.layer) {
            listenerKey = this.layer.on('postrender', animate);
        }
        if (this.identifyOptions.icon) {
            this.createMarker(feature);
            this.marker && this.GIS.Map.olObject!.addOverlay(this.marker);
        }
    };
}

export default Identify;
